import { WS_IDLE_TIMEOUT_MS, type ServerWsMessage } from '@et/shared';
import { session } from './client';

/**
 * Reconnecting WebSocket client (API_DOCS.md — WebSocket Protocol). Pings well inside the
 * server's 30 s idle window, reconnects with exponential backoff (1 s → 30 s), and calls
 * `onReconnect` so stores re-fetch REST state — anything that happened while the socket
 * was down is otherwise invisible.
 */

export type WsStatus = 'connecting' | 'open' | 'closed';

export interface WsClientOptions {
  onMessage: (m: ServerWsMessage) => void;
  onStatus: (s: WsStatus) => void;
  onReconnect: () => void;
}

export function connectWs(opts: WsClientOptions): () => void {
  let socket: WebSocket | null = null;
  let attempt = 0;
  let stopped = false;
  let everOpened = false;
  let ping: number | undefined;
  let retry: number | undefined;

  const open = (): void => {
    const token = session.token();
    if (stopped || !token) return;
    opts.onStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    socket = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(token)}`);

    socket.onopen = () => {
      attempt = 0;
      opts.onStatus('open');
      if (everOpened) opts.onReconnect();
      everOpened = true;
      ping = window.setInterval(() => socket?.send(JSON.stringify({ type: 'ping' })), WS_IDLE_TIMEOUT_MS / 3);
    };
    socket.onmessage = (ev: MessageEvent<string>) => {
      try {
        opts.onMessage(JSON.parse(ev.data) as ServerWsMessage);
      } catch {
        // ignore a malformed frame; the next one will be fine
      }
    };
    socket.onclose = () => {
      window.clearInterval(ping);
      opts.onStatus('closed');
      if (stopped) return;
      const delay = Math.min(30_000, 1000 * 2 ** attempt) * (0.8 + Math.random() * 0.4);
      attempt += 1;
      retry = window.setTimeout(open, delay);
    };
  };

  open();
  return () => {
    stopped = true;
    window.clearInterval(ping);
    window.clearTimeout(retry);
    socket?.close();
  };
}
